// Phase 5.9 · 관리자 쿠폰 + 공동구매 관리 페이지 (통합)
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Users, Send, Ticket, Package } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;
const badge = (s) => ({
  scheduled: 'bg-gray-200 text-gray-700',
  live: 'bg-[#FF3B30] text-white',
  target_reached: 'bg-emerald-600 text-white',
  ended: 'bg-zinc-500 text-white',
  cancelled: 'bg-orange-500 text-white',
  active: 'bg-emerald-500/10 text-emerald-700',
  disabled: 'bg-gray-200 text-gray-500',
}[s] || 'bg-gray-100 text-gray-500');

function AdminCouponModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', kind: 'amount', discount_value: 3000,
    min_order_amount: 0, max_discount: 0, valid_to: '', total_quota: 0, per_user_limit: 1,
    applies_all_products: true, scope: 'all' });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) { toast.error('쿠폰명을 입력해주세요.'); return; }
    setBusy(true);
    try {
      await api.post('/admin/coupons', {
        ...form,
        discount_value: Number(form.discount_value) || 0,
        min_order_amount: Number(form.min_order_amount) || 0,
        max_discount: Number(form.max_discount) || 0,
        total_quota: Number(form.total_quota) || 0,
        per_user_limit: Number(form.per_user_limit) || 1,
        valid_to: form.valid_to || null,
      });
      toast.success('관리자 쿠폰 생성');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '생성 실패'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-coupon-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white dark:bg-[#141414] shadow-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black">관리자 쿠폰 생성</h3>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded"><X size={18}/></button>
        </div>
        <div className="space-y-3 text-sm">
          <input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="쿠폰명 (예: LIVEMOA 가입 감사 3,000원)"
            className="w-full px-3 py-2 rounded border border-black/10 bg-transparent" data-testid="admin-coupon-name"/>
          <div>
            <span className="text-xs font-bold text-gray-500">종류</span>
            <div className="mt-1 inline-flex rounded-full border border-black/10 overflow-hidden">
              <button onClick={() => setField('kind', 'amount')} className={`px-3 py-1.5 text-xs font-bold ${form.kind === 'amount' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="admin-coupon-kind-amount">금액</button>
              <button onClick={() => setField('kind', 'percent')} className={`px-3 py-1.5 text-xs font-bold ${form.kind === 'percent' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="admin-coupon-kind-percent">%</button>
              <button onClick={() => setField('kind', 'free_ship')} className={`px-3 py-1.5 text-xs font-bold ${form.kind === 'free_ship' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="admin-coupon-kind-freeship">무료배송</button>
            </div>
          </div>
          {form.kind !== 'free_ship' && (
            <input type="number" value={form.discount_value} onChange={(e) => setField('discount_value', e.target.value)}
              placeholder={form.kind === 'percent' ? '할인율 (%)' : '할인금액 (원)'}
              className="w-full px-3 py-2 rounded border border-black/10 bg-transparent" data-testid="admin-coupon-discount"/>
          )}
          <input type="number" value={form.min_order_amount} onChange={(e) => setField('min_order_amount', e.target.value)}
            placeholder="최소 주문금액 (원)" className="w-full px-3 py-2 rounded border border-black/10 bg-transparent" data-testid="admin-coupon-min"/>
          <input type="datetime-local" value={form.valid_to} onChange={(e) => setField('valid_to', e.target.value)}
            className="w-full px-3 py-2 rounded border border-black/10 bg-transparent" data-testid="admin-coupon-valid-to"/>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-bold border border-black/10">취소</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-full text-sm font-black bg-[#FF3B30] text-white disabled:opacity-50" data-testid="admin-coupon-submit">생성</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCouponsGroupBuys({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('coupons');
  const [coupons, setCoupons] = useState([]);
  const [gbs, setGbs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, g] = await Promise.all([
        api.get('/admin/coupons'), api.get('/admin/group-buys')
      ]);
      setCoupons(c.data?.items || []);
      setGbs(g.data?.items || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const issueAll = async (cid) => {
    if (!window.confirm('LIVEMOA 전체 회원에게 이 쿠폰을 발급하시겠습니까?')) return;
    try {
      const r = await api.post(`/admin/coupons/${cid}/issue`, { target: 'all' });
      toast.success(`전체 발급 완료 · ${r.data.issued}건`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '발급 실패'); }
  };

  return (
    <AdminLayout user={user} active="coupons">
      <div className="max-w-5xl mx-auto space-y-5" data-testid="admin-coupons-gb-page">
        <h1 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>쿠폰 · 공동구매 관리</h1>
      <div className="inline-flex rounded-full border border-black/10 overflow-hidden">
        <button onClick={() => setTab('coupons')} className={`px-4 py-2 text-sm font-bold ${tab === 'coupons' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="admin-tab-coupons">
          <Ticket size={12} className="inline mr-1"/> 쿠폰 ({coupons.length})
        </button>
        <button onClick={() => setTab('groupbuys')} className={`px-4 py-2 text-sm font-bold ${tab === 'groupbuys' ? 'bg-[#FF3B30] text-white' : ''}`} data-testid="admin-tab-groupbuys">
          <Package size={12} className="inline mr-1"/> 공동구매 ({gbs.length})
        </button>
      </div>

      {tab === 'coupons' && (
        <div className="rounded-2xl border border-black/10 p-4">
          <div className="flex justify-between mb-3">
            <p className="text-sm font-black">전체 쿠폰</p>
            <button onClick={() => setModalOpen(true)} className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white inline-flex items-center gap-1" data-testid="admin-coupon-new">
              <Plus size={12}/> 관리자 쿠폰 만들기
            </button>
          </div>
          {coupons.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">쿠폰이 없습니다.</p>
          ) : (
            <div className="space-y-2">
              {coupons.map((c) => (
                <div key={c.coupon_id} className="p-3 rounded-lg border border-black/5 flex items-center gap-3 flex-wrap" data-testid={`admin-coupon-row-${c.coupon_id}`}>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${c.issuer === 'admin' ? 'bg-black text-white' : 'bg-blue-100 text-blue-700'}`}>{c.issuer}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${badge(c.status)}`}>{c.status}</span>
                  <span className="font-bold text-sm flex-1 truncate">{c.name}</span>
                  <span className="text-xs font-black text-[#FF3B30]">{c.kind === 'free_ship' ? '무료배송' : c.kind === 'percent' ? `${c.discount_value}%` : won(c.discount_value)}</span>
                  <span className="text-xs text-gray-500">발급 {c.issued_count || 0} / 사용 {c.used_count || 0}</span>
                  {c.issuer === 'admin' && c.status === 'active' && (
                    <button onClick={() => issueAll(c.coupon_id)} className="px-2 py-1 text-xs font-bold bg-black text-white rounded-full inline-flex items-center gap-1" data-testid={`admin-issue-all-${c.coupon_id}`}>
                      <Send size={11}/> 전체 발급
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'groupbuys' && (
        <div className="rounded-2xl border border-black/10 p-4">
          <p className="text-sm font-black mb-3">전체 공동구매 (읽기 전용)</p>
          {gbs.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">공동구매가 없습니다.</p>
          ) : (
            <div className="space-y-2">
              {gbs.map((gb) => (
                <div key={gb.group_buy_id} className="p-3 rounded-lg border border-black/5 flex items-center gap-3 flex-wrap" data-testid={`admin-gb-row-${gb.group_buy_id}`}>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${badge(gb.status)}`}>{gb.status}</span>
                  <span className="font-bold text-sm flex-1 truncate">{gb.product_name}</span>
                  <span className="text-xs text-gray-500 inline-flex items-center gap-1"><Users size={11}/> {gb.current_count}/{gb.target}</span>
                  <span className="text-xs text-gray-500">{gb.seller_id}</span>
                  <span className="text-xs font-black text-[#FF3B30]">{won(gb.base_price)}</span>
                  {gb.final_price != null && <span className="text-xs font-black text-emerald-600">→ {won(gb.final_price)}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AdminCouponModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load}/>
      </div>
    </AdminLayout>
  );
}
